/**
 * Auth API
 *
 * API functions for authentication and current user info.
 */

import { api } from "./client";
import { resetAuthRefreshThrottle } from "../stores/authRefreshThrottle";
import { setCurrentUser } from "../stores/currentUserStore";
import { clearOnboardingInFlight } from "../stores/onboardingResume";
import type { AuthMeResponse, RequestAccessResponse } from "./types";

export async function getMe(): Promise<AuthMeResponse> {
  return api.get<AuthMeResponse>("/auth/me");
}

export async function logout(): Promise<void> {
  // Only a logout the server actually processed may clear client identity or
  // redirect. The `session` cookie is HttpOnly, so a failed POST leaves both
  // it and the `sessions` row live and the client cannot revoke either on its
  // own; redirecting anyway would report a logout that did not happen, since
  // LoginPage's onMount re-probes /auth/me, still succeeds, and bounces
  // straight back into the app. Letting the error propagate instead keeps the
  // user on an authenticated page, where a retry is possible.
  await api.post<null>("/auth/logout", {});

  // Reset the module-scope identity state explicitly rather than relying on
  // the realm teardown the navigation below happens to cause (FLOW-984), so
  // no cached user or armed refresh timer can outlive this session.
  setCurrentUser(null);
  resetAuthRefreshThrottle();
  // The in-flight onboarding marker (FLOW-991) names a project id, so it is
  // session state too: the next user on this browser must not inherit it.
  clearOnboardingInFlight();
  window.location.href = "/login";
}

export async function requestAccess(): Promise<RequestAccessResponse> {
  return api.post<RequestAccessResponse>("/auth/request-access", {});
}
